import type React from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import "remirror/styles/all.css"

import { useNavigate } from "react-router-dom"

import EditorWrapper, { type RemirrorInnerEditorProps } from "./editor"
import createExtension from "./extensions"
import { DefaultPreset } from "./extensions/default"
import type { Preset } from "./types"
import ReactTextEditor from "./view"

export type RemirrorProps = {
  options?: Preset
  children?: React.ReactNode
  disableBorder?: boolean

  settings?: {
    uploadOptions?: { forType: string; forId: string }
    uploadFile?: (file: File, options: { forType: string; forId: string }) => Promise<string>
    onExternalLink?: (url: string) => void
    fullMentionLinks?: boolean
  }
  historyEnabled?: boolean
} & Omit<RemirrorInnerEditorProps, "children" | "extensions">

const localRegex = /((^| )(http:\/\/www.https:\/\/www.|http:\/\/|https:\/\/)(([a-z]{1,15}.)?simplydo.co.uk(\/| |$)))/gi

const checkLocalLink = (href) => {
  if (href.startsWith("/")) {
    return { isLocal: true, path: href }
  }

  const match = href.match(localRegex)
  return {
    isLocal: !!match,
    path: match ? `/${href.replace(localRegex, "")}` : href,
  }
}

const getExtensions = (
  options: RemirrorProps["options"],
  settings: RemirrorProps["settings"] = {},
  readOnly: boolean,
  navigate: (path: string) => void,
) => {
  const order = []

  const combinedOptions = options.reduce((p, { name, config = {} }) => {
    if (!order.includes(name)) {
      order.push(name)
    }

    // Workaround for injecting `uploadHandler` for images during paste actions
    if ("uploadFile" in config && "uploadOptions" in settings) {
      config.uploadHandler = (files) => {
        const promises = files.map(
          ({ file, progress }, i) =>
            () =>
              config.uploadFile(file, settings.uploadOptions).then((src) => {
                progress((i + 1) / files.length)
                return { src, name: file.name, fileName: file.name }
              }),
        )

        return promises
      }
    }

    // Workaround for image resizing not respecting Remirror `editable`
    if (name === "image") {
      // @ts-ignore
      config.enableResizing = !readOnly
    }

    // Set fullMentionLinks to true to get full links instead of relative links
    if (name === "mention") {
      if (settings.fullMentionLinks) {
        // @ts-ignore
        const relativeFormatMention = config.formatMention ?? ((item) => item)
        // @ts-ignore
        config.formatMention = (item) => {
          const formatted = relativeFormatMention(item)
          return {
            ...formatted,
            href: window.location.origin + formatted.href,
          }
        }
      }
    }

    const prev = p[name]
    if (!prev) {
      p[name] = [config]
    } else {
      p[name] = [...prev, config]
    }
    return p
  }, {})

  const initialisedExtensions = order.map((name) => {
    const extension = createExtension(name, combinedOptions[name])

    if (name === "link") {
      extension.addHandler("onClick", (_event, data) => {
        if (!readOnly) {
          return false
        }
        const localLink = checkLocalLink(data.href)
        if (localLink.isLocal) {
          navigate(localLink.path)
        } else {
          if (settings.onExternalLink) {
            settings.onExternalLink(data.href)
          } else {
            window.open(data.href, "_blank")
          }
        }

        return true
      })
    }
    if (name === "mention") {
      extension.addHandler("onClick", (_event, data) => {
        if (!readOnly) {
          return false
        }
        const atomName = data?.node?.attrs["data-mention-atom-name"]
        const atomId = data?.node?.attrs["data-mention-atom-id"]
        if (!atomName || !atomId) {
          return false
        }
        navigate(`/${atomName}/${atomId}`)

        return true
      })
    }

    return extension
  })
  return { initialisedExtensions, combinedOptions }
}

export const RemirrorEditor = ({
  value,
  onChange,
  onChangeJson,
  onFocus,
  onBlur,
  emptyReturnValue,
  options = DefaultPreset,
  readOnly = false,
  placeholder = null,
  disableBorder,
  settings,
  editorRef,
  handleKeyDown,
  children,
}: RemirrorProps) => {
  const navigate = useNavigate()

  const [extensions, setExtensions] = useState(() => getExtensions(options, settings, readOnly, navigate))
  const [_enabledExtensions, setEnabledExtensions] = useState([])
  const { initialisedExtensions, combinedOptions } = extensions

  useEffect(() => {
    setExtensions(getExtensions(options, settings, readOnly, navigate))
    setEnabledExtensions(options.map((o) => o.name))
  }, [options, settings, readOnly, navigate])

  const focusHandler = useCallback(
    (val) => {
      onFocus?.(val)
    },
    [onFocus],
  )

  const blurHandler = useCallback(
    (html, textValue) => {
      onBlur?.(html, textValue)
    },
    [onBlur],
  )

  const realValue = useMemo(() => value?.split("\n")?.join("<p>") ?? emptyReturnValue, [value, emptyReturnValue])

  return (
    <EditorWrapper
      placeholder={placeholder}
      readOnly={readOnly}
      value={realValue}
      onChange={onChange}
      onChangeJson={onChangeJson}
      onBlur={blurHandler}
      onFocus={focusHandler}
      emptyReturnValue={emptyReturnValue}
      extensions={initialisedExtensions}
      editorRef={editorRef}
      handleKeyDown={handleKeyDown}
    >
      {children}
      <ReactTextEditor disableBorder={disableBorder} />
    </EditorWrapper>
  )
}
